import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import axiosClient from "../../../../api-client/AxiosClient";
import { toast, ToastContainer } from "react-toastify";
import Select, { components } from "react-select";
import { Trash2 } from "lucide-react";
import { FaEdit } from "react-icons/fa";

const USER_MGMT_TOAST_ID = "user-management-popups";

// Helper function for button hover handlers
const createHoverHandlers = (hoverColor, defaultColor, hoverTextColor = null, defaultTextColor = null) => ({
  onMouseOver: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onMouseOut: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
  onFocus: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onBlur: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
});

// Helper functions for status styling
const getStatusBackgroundColor = (isActive) => {
  return isActive ? "#dcfce7" : "#fee2e2";
};

const getStatusTextColor = (isActive) => {
  return isActive ? "#16a34a" : "#dc2626";
};

const getStatusBorderColor = (isActive) => {
  return isActive ? "#bbf7d0" : "#fecaca";
};

const getStatusText = (isActive) => {
  return isActive ? "Active" : "Inactive";
};

// Custom Option component with checkbox
const Option = (props) => {
  const selectedValues = props.selectProps.value || [];
  const isSelected = Array.isArray(selectedValues)
    ? selectedValues.some((item) => item.value === props.value)
    : props.isSelected;
  const isMulti = Boolean(props.selectProps?.isMulti);

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {isMulti && (
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            style={{
              width: "16px",
              height: "16px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
        )}
        <span
          style={{
            overflow: isMulti ? "hidden" : "visible",
            textOverflow: isMulti ? "ellipsis" : "unset",
            whiteSpace: isMulti ? "nowrap" : "normal",
          }}
        >
          {props.label}
        </span>
      </div>
    </components.Option>
  );
};

Option.propTypes = {
  selectProps: PropTypes.shape({
    isMulti: PropTypes.bool,
    value: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })),
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

// Custom MenuList component with Select All option
const MenuList = (props) => {
  const { options, getValue, setValue } = props;
  const selectedValues = getValue();
  const allSelected = selectedValues.length === options.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setValue([], "clear");
    } else {
      setValue(options, "set-value");
    }
  };

  return (
    <components.MenuList {...props}>
      <button
        type="button"
        onClick={handleSelectAll}
        style={{
          width: "100%",
          padding: "10px 12px",
          cursor: "pointer",
          border: "none",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
          fontWeight: "600",
          color: "#1e40af",
          textAlign: "left",
        }}
        {...createHoverHandlers("#eff6ff", "#f9fafb")}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={allSelected}
            readOnly
            style={{
              width: "16px",
              height: "16px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
          <span>Select All</span>
        </div>
      </button>
      {props.children}
    </components.MenuList>
  );
};

MenuList.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  getValue: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

// Custom ValueContainer to show only 4 items
const ValueContainer = ({ children, ...props }) => {
  const selectedValues = props.getValue();
  const MAX_VISIBLE = 4;

  if (!selectedValues || selectedValues.length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const hiddenCount = selectedValues.length - MAX_VISIBLE;

  return (
    <components.ValueContainer {...props}>
      {React.Children.toArray(children).map((child, index) => {
        if (child?.type === components.MultiValue) {
          const valueIndex = selectedValues.findIndex(
            (v) => v.value === child.props.data.value
          );
          if (valueIndex >= MAX_VISIBLE) {
            return null;
          }
        }
        return child;
      })}
      {hiddenCount > 0 && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 8px",
            backgroundColor: "#f3f4f6",
            borderRadius: "6px",
            fontSize: "13px",
            color: "#6b7280",
            fontWeight: "600",
            marginLeft: "4px",
          }}
        >
          +{hiddenCount} more
        </div>
      )}
    </components.ValueContainer>
  );
};

ValueContainer.propTypes = {
  children: PropTypes.node.isRequired,
  getValue: PropTypes.func.isRequired,
};

// Professional Modal Component
const UserModal = ({ show, title, onClose, children }) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "1000px",
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "24px 32px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "700",
              color: "#111827",
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "28px",
              cursor: "pointer",
              color: "#6b7280",
              lineHeight: 1,
              padding: 0,
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              transition: "all 0.2s",
            }}
            {...createHoverHandlers("#f3f4f6", "transparent")}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

UserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const getRoleIdFromRoles = (roles, roleValue) => {
  const role = roles.find((r) => {
    const roleIdMatches = String(r?.id ?? "") === String(roleValue ?? "");
    const roleNameMatches =
      r?.name?.toLowerCase() === String(roleValue ?? "").toLowerCase();
    return roleIdMatches || roleNameMatches;
  });
  return role?.id;
};

// Helper function to initialize edit form
const initializeEditForm = (user) => {
  return {
    email: user.email || "",
    firstName: user.first_name || "",
    lastName: user.last_name || "",
    isActive: user.isactive?.toString?.() ?? "true",
  };
};

// Helper function to reset form
const getResetForm = () => ({
  email: "",
  firstName: "",
  lastName: "",
  isActive: "true",
});

const createAccessRow = (overrides = {}) => ({
  id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
  applicationId: "",
  organizationId: "",
  organizationConfigId: "",
  roleId: "",
  ...overrides,
});

const createDefaultAccessRows = () => [createAccessRow()];

const extractUserAccessList = (user) => {
  if (Array.isArray(user?.accessList)) return user.accessList;
  if (Array.isArray(user?.access_list)) return user.access_list;
  if (Array.isArray(user?.lstAccessList)) return user.lstAccessList;
  if (Array.isArray(user?.userAccesses)) return user.userAccesses;
  return [];
};

// Helper function to handle edit button click
const handleEditClick = (user, roles, setters) => {
  const { setShowAdd, setEditUserId, setAddForm, setAccessRows } = setters;
  setShowAdd(true);
  setEditUserId(user.id);
  setAddForm(initializeEditForm(user));

  const accessList = extractUserAccessList(user);
  if (accessList.length > 0) {
    setAccessRows(
      accessList.map((a) =>
        createAccessRow({
          applicationId: String(a.applicationId ?? a.application_id ?? ""),
          organizationId: String(a.organizationId ?? a.organization_id ?? ""),
          organizationConfigId: String(
            a.organizationConfigId ?? a.organization_config_id ?? ""
          ),
          roleId: String(
            a.roleId ?? a.role_id ?? getRoleIdFromRoles(roles, a.role) ?? ""
          ),
        })
      )
    );
    return;
  }

  // Fallback: use orgList + user's role as prefill if backend doesn't send accessList.
  const fallbackRoleId = getRoleIdFromRoles(roles, user.role);
  const orgList = Array.isArray(user?.orgList) ? user.orgList : [];
  if (orgList.length > 0) {
    setAccessRows(
      orgList.map((org) =>
        createAccessRow({
          organizationId: String(
            org?.organizationId ?? org?.organization_id ?? org?.value ?? ""
          ),
          roleId: fallbackRoleId ? String(fallbackRoleId) : "",
        })
      )
    );
    return;
  }

  setAccessRows([
    createAccessRow({
      roleId: fallbackRoleId ? String(fallbackRoleId) : "",
    }),
  ]);
};

// Helper function to handle delete button click
const handleDeleteClick = (userId, setters) => {
  const { setEditUserId, setShowDeleteModal } = setters;
  setEditUserId(userId);
  setShowDeleteModal(true);
};



export default function UserManagement() {
  const { UserMgmt } = JSON.parse(
    sessionStorage.getItem("rolesAndPermission") || "{}"
  );

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const [addForm, setAddForm] = useState(getResetForm());
  const [editUserId, setEditUserId] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [applicationOptions, setApplicationOptions] = useState([]);
  const [configOptionsByOrgId, setConfigOptionsByOrgId] = useState({});
  const [configsLoadingByOrgId, setConfigsLoadingByOrgId] = useState({});
  const [accessRows, setAccessRows] = useState(createDefaultAccessRows());
  const configFetchInFlightRef = useRef({});
  const menuPortalTarget =
    typeof document === "undefined" ? null : document.body;

  // Create setters object for helper functions
  const formSetters = {
    setShowAdd,
    setEditUserId,
    setAddForm,
    setAccessRows,
    setShowDeleteModal,
  };

  // Add this validation function after formSetters
  const isAccessRowValid = (row) => {
    return (
      Boolean(row?.applicationId) &&
      Boolean(row?.organizationId) &&
      Boolean(row?.organizationConfigId) &&
      Boolean(row?.roleId)
    );
  };

  const isFormValid = () => {
    return (
      addForm.email.trim() !== "" &&
      addForm.firstName.trim() !== "" &&
      addForm.lastName.trim() !== "" &&
      addForm.isActive !== "" &&
      accessRows.length > 0 &&
      accessRows.every(isAccessRowValid)
    );
  };

  // Fetch functions
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get("/api/User/all");
      setUsers(response.data || []);
      setError(null);
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to load user data.";
      setError(errorMessage);
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axiosClient.get(
        "/api/Organization/GetUserOrganizations"
      );
      const options = response.data?.map((item) => ({
        value: item.organizationId,
        label: item.name,
      }));
      setAllOrganizations(options || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setAllOrganizations([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosClient.get("/api/User/Roles");
      setRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axiosClient.get(
        "/api/Organization/applications"
      );
      const options =
        response.data?.map((item) => {
          const value = String(
            item?.applicationId ?? item?.application_id ?? item?.id ?? ""
          );
          const label =
            item?.name ??
            item?.applicationName ??
            item?.description ??
            (value ? String(value) : "");
          return value ? { value, label } : null;
        }) || [];

      setApplicationOptions(options.filter(Boolean));
    } catch (error) {
      console.error("Error fetching applications:", error);
      setApplicationOptions([]);
    }
  };

  const ensureOrganizationConfigs = async (orgId) => {
    if (!orgId) return;

    if (Object.hasOwn(configOptionsByOrgId, orgId)) {
      return;
    }
    if (configFetchInFlightRef.current?.[orgId]) return;

    configFetchInFlightRef.current[orgId] = true;
    setConfigsLoadingByOrgId((prev) => ({ ...prev, [orgId]: true }));

    try {
      const response = await axiosClient.get(
        `/api/Organization/GetOrganizationConfigs?orgId=${orgId}`
      );
      const configs = Array.isArray(response?.data) ? response.data : [];

      const options = configs
        .map((cfg) => {
          const value = String(
            cfg?.organizationConfigId ??
            cfg?.organization_config_id ??
            cfg?.id ??
            ""
          );
          const source = cfg?.sourceName ?? cfg?.source_name ?? "";
          const target = cfg?.targetName ?? cfg?.target_name ?? "";

          const fallbackLabel = value ? String(value) : "";
          const label =
            source && target
              ? `${source} -> ${target}`
              : cfg?.domainName ?? cfg?.domain_name ?? fallbackLabel;

          return value ? { value, label } : null;
        })
        .filter(Boolean);

      setConfigOptionsByOrgId((prev) => ({ ...prev, [orgId]: options }));
    } catch (error) {
      console.error("Error fetching organization configs:", error);
      setConfigOptionsByOrgId((prev) => ({ ...prev, [orgId]: [] }));
    } finally {
      configFetchInFlightRef.current[orgId] = false;
      setConfigsLoadingByOrgId((prev) => ({ ...prev, [orgId]: false }));
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchOrganizations();
    fetchApplications();
  }, []);

  useEffect(() => {
    const orgIds = [
      ...new Set(accessRows.map((row) => row?.organizationId).filter(Boolean)),
    ];

    orgIds.forEach((orgId) => {
      const hasConfigs = Object.hasOwn(configOptionsByOrgId, orgId);

      if (!hasConfigs) {
        ensureOrganizationConfigs(orgId);
      }
    });
  }, [accessRows]);

  // Filter and sort users
  const filteredUsers = users.filter((u) =>
    `${u.first_name || ""} ${u.last_name || ""}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const nameA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
    const nameB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
    if (nameA < nameB) return sortAsc ? -1 : 1;
    if (nameA > nameB) return sortAsc ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedUsers.length / pageSize);
  const pagedUsers = sortedUsers.slice((page - 1) * pageSize, page * pageSize);

  // Helper function to get colSpan for table cells
  const getTableColSpan = () => {
    return (UserMgmt?.canDelete || UserMgmt?.canUpdate) ? 5 : 4;
  };

  // Helper function to render users table content
  const renderUsersTableContent = () => {
    if (pagedUsers.length === 0) {
      return (
        <tr>
          <td
            colSpan={getTableColSpan()}
            style={{
              padding: "48px",
              textAlign: "center",
              color: "#9ca3af",
            }}
          >
            No users found.
          </td>
        </tr>
      );
    }

    return pagedUsers.map((user, idx) => (
      <tr
        key={user.id}
        style={{
          backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb",
          transition: "background-color 0.2s",
        }}
        {...createHoverHandlers("#eff6ff", idx % 2 === 0 ? "white" : "#f9fafb")}
      >
        <td
          style={{
            padding: "16px 20px",
            color: "#111827",
            fontWeight: "500",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          {`${user.first_name || ""} ${user.last_name || ""}`.trim()}
        </td>
        <td
          style={{
            padding: "16px 20px",
            color: "#6b7280",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          {user.email}
        </td>

        <td
          style={{
            padding: "16px 20px",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "600",
              backgroundColor: getStatusBackgroundColor(user.isactive),
              color: getStatusTextColor(user.isactive),
              border: `1px solid ${getStatusBorderColor(user.isactive)}`,
            }}
          >
            {getStatusText(user.isactive)}
          </span>
        </td>
        <td
          style={{
            padding: "16px 20px",
            color: "#6b7280",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          {user.created_at}
        </td>
        {(UserMgmt?.canDelete || UserMgmt?.canUpdate) && (
          <td
            style={{
              padding: "16px 20px",
              borderBottom:
                idx === pagedUsers.length - 1
                  ? "none"
                  : "1px solid #e5e7eb",
            }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              {UserMgmt?.canUpdate && (
                <button
                  onClick={() => handleEditClick(user, roles, formSetters)}
                  style={{
                    padding: "8px",
                    borderRadius: "9999px",
                    border: "none",
                    backgroundColor: "transparent",
                    color: "#3b82f6",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  {...createHoverHandlers("#dbeafe", "transparent", "#2563eb", "#3b82f6")}
                  title="Edit User"
                >
                  <FaEdit size={15} />
                </button>
              )}
              {UserMgmt?.canDelete && (
                <button
                  onClick={() => handleDeleteClick(user.id, formSetters)}
                  style={{
                    padding: "8px",
                    borderRadius: "9999px",
                    border: "none",
                    backgroundColor: "transparent",
                    color: "#ef4444",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  {...createHoverHandlers("#fee2e2", "transparent", "#dc2626", "#ef4444")}
                  title="Delete User"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </td>
        )}
      </tr>
    ));
  };

  // Handlers
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDeleteUser = async () => {
    try {
      await axiosClient.delete(`/api/User/${editUserId}`);
      setShowDeleteModal(false);
      toast.success("User deleted successfully.", { containerId: USER_MGMT_TOAST_ID });
      fetchUsers();
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Something went wrong while deleting user. Please try again later!";
      toast.error(errorMessage, { containerId: USER_MGMT_TOAST_ID });
      console.error("Error deleting user:", err);
    }
  };

  const checkForDuplicateAccess = (rows) => {
    return rows.some((row, index) => {
      return rows.findIndex((r) =>
        r.applicationId === row.applicationId &&
        r.organizationId === row.organizationId &&
        r.organizationConfigId === row.organizationConfigId &&
        r.roleId === row.roleId
      ) !== index;
    });
  };

  const createUserPayload = (form, rows) => {
    const accessList = rows.map((row) => ({
      applicationId: row.applicationId,
      organizationId: row.organizationId,
      organizationConfigId: row.organizationConfigId,
      roleId: row.roleId,
    }));

    const uniqueOrgIds = [...new Set(accessList.map((item) => item.organizationId))];

    return {
      id: 0,
      email: form.email,
      password: "Pass@123",
      firstName: form.firstName,
      lastName: form.lastName,
      role: rows[0]?.roleId ?? "",
      organisation: uniqueOrgIds.join(", "),
      createdAt: new Date().toISOString(),
      isActive: form.isActive === "true",
      accessList,
    };
  };

  const handleSubmit = async () => {
    setAddLoading(true);

    if (!accessRows.every(isAccessRowValid)) {
      toast.error(
        "Please select Product, Organization, Configuration and Role for every access row.",
        { containerId: USER_MGMT_TOAST_ID }
      );
      setAddLoading(false);
      return;
    }

    if (checkForDuplicateAccess(accessRows)) {
      toast.error(
        "Duplicate access permissions detected. Each combination must be unique.",
        { containerId: USER_MGMT_TOAST_ID }
      );
      setAddLoading(false);
      return;
    }

    try {
      const payload = createUserPayload(addForm, accessRows);

      if (editUserId) {
        await axiosClient.put(`/api/User/${editUserId}`, payload);
        toast.success("User updated successfully.", { containerId: USER_MGMT_TOAST_ID });
      } else {
        await axiosClient.post("/api/User", payload);
        toast.success("User created successfully.", { containerId: USER_MGMT_TOAST_ID });
      }

      setShowAdd(false);
      setAddForm(getResetForm());
      setAccessRows(createDefaultAccessRows());
      setEditUserId(null);
      fetchUsers();
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to save user. Please check the details.";
      toast.error(errorMessage, { containerId: USER_MGMT_TOAST_ID });
      console.error("Error saving user:", err);
    } finally {
      setAddLoading(false);
    }
  };

  const handleAddAccessRow = () => {
    setAccessRows((prev) => [...prev, createAccessRow()]);
  };

  const handleRemoveAccessRow = (rowId) => {
    setAccessRows((prev) => {
      if (prev.length <= 1) {
        return [
          {
            ...prev[0],
            applicationId: "",
            organizationId: "",
            organizationConfigId: "",
            roleId: "",
          },
        ];
      }
      return prev.filter((row) => row.id !== rowId);
    });
  };

  const handleAccessRowChange = (rowId, field, value) => {
    setAccessRows((prev) => {
      const updatedRows = prev.map((row) => {
        if (row.id !== rowId) return row;
        if (field === "organizationId") {
          return {
            ...row,
            organizationId: value,
            organizationConfigId: "", // Reset config when org changes
          };
        }
        return { ...row, [field]: value };
      });

      // Show real-time warning if a duplicate is detected
      const currentRow = updatedRows.find((r) => r.id === rowId);
      if (isAccessRowValid(currentRow)) {
        const isDuplicate = updatedRows.some(
          (r) =>
            r.id !== rowId &&
            isAccessRowValid(r) &&
            r.applicationId === currentRow.applicationId &&
            r.organizationId === currentRow.organizationId &&
            r.organizationConfigId === currentRow.organizationConfigId &&
            r.roleId === currentRow.roleId
        );
        if (isDuplicate) {
          toast.warn("This access combination already exists for this user.", {
            containerId: USER_MGMT_TOAST_ID,
          });
        }
      }

      return updatedRows;
    });
  };

  // Professional Select Styles
  const selectStyles = {
    container: (base) => ({ ...base, width: "100%" }),
    control: (base, state) => ({
      ...base,
      minHeight: "36px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": { borderColor: "#3b82f6" },
      backgroundColor: state.isDisabled ? "#f9fafb" : "white",
      cursor: state.isDisabled ? "not-allowed" : "default",
      outline: "none",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "4px 8px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "13px",
      lineHeight: "18px",
      color: "#1f2937",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      "input:focus": {
        boxShadow: "none",
        outline: "none",
      },
      fontSize: "13px",
    }),
    menu: (base) => ({ ...base, zIndex: 10001 }),
    menuPortal: (base) => ({ ...base, zIndex: 10001 }),
    menuList: (base) => ({ ...base, maxHeight: "200px" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#eff6ff" : "white",
      color: state.isSelected ? "#1e40af" : "#1f2937",
      cursor: "pointer",
      padding: "8px 10px",
      fontSize: "13px",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#dbeafe",
      borderRadius: "6px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      padding: "3px 6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#1e40af",
      ":hover": { backgroundColor: "#bfdbfe", color: "#1e3a8a" },
    }),
  };
  const fieldStyle = {
    marginBottom: "16px",
  };

  const labelStyle = {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
  };

  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.2s",
    boxSizing: "border-box",
  };

  const inputFocusStyle = {
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
  };

  return (
    <div
      style={{
        borderRadius: "16px",
        backgroundColor: "white",
        padding: "24px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginTop: "24px",
      }}
    >
      <h2
        style={{
          fontSize: "28px",
          fontWeight: "700",
          marginBottom: "8px",
          color: "#111827",
        }}
      >
        User Management
      </h2>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={handleSearch}
            style={{
              ...inputStyle,
              width: "300px",
              minWidth: "250px",
            }}
            onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
            onBlur={(e) => {
              e.target.style.borderColor = "#d1d5db";
              e.target.style.boxShadow = "none";
            }}
          />
          {UserMgmt?.canAdd && (
            <button
              onClick={() => {
                setShowAdd(true);
                setEditUserId(null);
                setAddForm(getResetForm());
                setAccessRows(createDefaultAccessRows());
              }}
              style={{
                backgroundColor: "#1e40af",
                color: "white",
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#1e3a8a", "#1e40af")}
            >
              <span style={{ fontSize: "20px", fontWeight: "700" }}>+</span> Add
              User
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <UserModal
        show={showAdd}
        title={editUserId ? "Edit User" : "Add User"}
        onClose={() => setShowAdd(false)}
      >
        <div>
          {/* Email */}
          <div style={fieldStyle}>
            <label htmlFor="user-email" style={labelStyle}>
              Email <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="user-email"
              type="email"
              placeholder="Enter email address"
              value={addForm.email}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, email: e.target.value }))
              }
              disabled={editUserId !== null}
              style={{
                ...inputStyle,
                backgroundColor: editUserId === null ? "white" : "#f9fafb",
                cursor: editUserId === null ? "text" : "not-allowed",
              }}
              onFocus={(e) =>
                editUserId === null && Object.assign(e.target.style, inputFocusStyle)
              }
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Name Row */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "16px" }}>
            {/* First Name */}
            <div style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}>
              <label htmlFor="user-first-name" style={labelStyle}>
                First Name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="user-first-name"
                type="text"
                placeholder="Enter first name"
                value={addForm.firstName}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, firstName: e.target.value }))
                }
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Last Name */}
            <div style={{ ...fieldStyle, flex: 1, marginBottom: 0 }}>
              <label htmlFor="user-last-name" style={labelStyle}>
                Last Name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="user-last-name"
                type="text"
                placeholder="Enter last name"
                value={addForm.lastName}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, lastName: e.target.value }))
                }
                style={inputStyle}
                onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {/* User Access Permissions */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#374151", marginBottom: "10px" }}>
              User Access Permissions
            </div>

            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                overflow: "visible",
                backgroundColor: "white",
              }}
            >
              <div style={{ overflowX: "auto", maxHeight: "245px", overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                    <tr style={{ backgroundColor: "#f3f4f6" }}>
                      <th style={{ textAlign: "left", padding: "12px 14px", fontSize: "13px", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb", minWidth: "150px", backgroundColor: "#f3f4f6" }}>
                        Product
                      </th>
                      <th style={{ textAlign: "left", padding: "12px 14px", fontSize: "13px", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb", minWidth: "200px", backgroundColor: "#f3f4f6" }}>
                        Organization
                      </th>
                      <th style={{ textAlign: "left", padding: "12px 14px", fontSize: "13px", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb", minWidth: "300px", backgroundColor: "#f3f4f6" }}>
                        Configuration
                      </th>
                      <th style={{ textAlign: "left", padding: "12px 14px", fontSize: "13px", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb", minWidth: "150px", backgroundColor: "#f3f4f6" }}>
                        Role
                      </th>
                      <th style={{ textAlign: "center", padding: "12px 14px", fontSize: "13px", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb", width: "80px", minWidth: "80px", backgroundColor: "#f3f4f6" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessRows.map((row, idx) => {
                      const productValueOption =
                        applicationOptions.find((opt) => opt.value === row.applicationId) ||
                        (row.applicationId
                          ? { value: row.applicationId, label: row.applicationId }
                          : null);
                      const orgValueOption =
                        allOrganizations.find((opt) => opt.value === row.organizationId) ||
                        (row.organizationId
                          ? { value: row.organizationId, label: row.organizationId }
                          : null);
                      const configOptionsForRow = row.organizationId
                        ? configOptionsByOrgId?.[row.organizationId] || []
                        : [];
                      const isConfigLoading = Boolean(configsLoadingByOrgId?.[row.organizationId]);
                      const configValueOption = (() => {
                        if (isConfigLoading && row.organizationConfigId) {
                          return { value: row.organizationConfigId, label: "Loading..." };
                        }
                        const found = configOptionsForRow.find((opt) => opt.value === row.organizationConfigId);
                        if (found) return found;
                        return row.organizationConfigId ? { value: row.organizationConfigId, label: row.organizationConfigId } : null;
                      })();

                      let configPlaceholder = "Select Configuration";
                      if (row.organizationId && configsLoadingByOrgId?.[row.organizationId]) {
                        configPlaceholder = "Loading...";
                      }

                      const roleOptions =
                        roles?.map((r) => ({ value: r.id, label: r.name })) ||
                        [];
                      const roleValueOption =
                        roleOptions.find((opt) => opt.value === row.roleId) ||
                        (row.roleId
                          ? { value: row.roleId, label: row.roleId }
                          : null);

                      return (
                        <tr key={row.id} style={{ backgroundColor: idx % 2 === 0 ? "white" : "#fafafa" }}>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e5e7eb" }}>
                            <Select
                              options={applicationOptions}
                              components={{ Option }}
                              value={productValueOption}
                              onChange={(selected) =>
                                handleAccessRowChange(
                                  row.id,
                                  "applicationId",
                                  selected?.value || ""
                                )
                              }
                              placeholder="Select Product"
                              styles={selectStyles}
                              isClearable={false}
                              isDisabled={applicationOptions.length === 0}
                              menuPlacement="bottom"
                              menuPosition="fixed"
                              menuPortalTarget={menuPortalTarget}
                            />
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e5e7eb" }}>
                            <Select
                              options={allOrganizations}
                              components={{ Option }}
                              value={orgValueOption}
                              onChange={(selected) =>
                                handleAccessRowChange(
                                  row.id,
                                  "organizationId",
                                  selected?.value || ""
                                )
                              }
                              placeholder="Select Organization"
                              styles={selectStyles}
                              isClearable={false}
                              isDisabled={allOrganizations.length === 0}
                              menuPlacement="bottom"
                              menuPosition="fixed"
                              menuPortalTarget={menuPortalTarget}
                            />
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e5e7eb" }}>
                            <Select
                              options={configOptionsForRow}
                              components={{ Option }}
                              value={configValueOption}
                              onChange={(selected) =>
                                handleAccessRowChange(
                                  row.id,
                                  "organizationConfigId",
                                  selected?.value || ""
                                )
                              }
                              placeholder={configPlaceholder}
                              styles={selectStyles}
                              isClearable={false}
                              isLoading={isConfigLoading}
                              isDisabled={
                                !row.organizationId ||
                                isConfigLoading
                              }
                              menuPlacement="bottom"
                              menuPosition="fixed"
                              menuPortalTarget={menuPortalTarget}
                            />
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e5e7eb" }}>
                            <Select
                              options={roleOptions}
                              components={{ Option }}
                              value={roleValueOption}
                              onChange={(selected) =>
                                handleAccessRowChange(
                                  row.id,
                                  "roleId",
                                  selected?.value || ""
                                )
                              }
                              placeholder="Select Role"
                              styles={selectStyles}
                              isClearable={false}
                              isDisabled={roles?.length === 0}
                              menuPlacement="bottom"
                              menuPosition="fixed"
                              menuPortalTarget={menuPortalTarget}
                            />
                          </td>
                          <td style={{ padding: "6px 10px", borderBottom: "1px solid #e5e7eb", textAlign: "center" }}>
                            <button
                              type="button"
                              onClick={() => handleRemoveAccessRow(row.id)}
                              style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                color: "#ef4444",
                              }}
                              title="Remove Access"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ padding: "14px 12px", display: "flex", justifyContent: "center" }}>
                <button
                  type="button"
                  onClick={handleAddAccessRow}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "1px solid #1d4ed8",
                    backgroundColor: "#1d4ed8",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "700",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  + Add Access
                </button>
              </div>
            </div>
          </div>

          {/* Active/Inactive Checkbox */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
            <input
              type="checkbox"
              id="user-isactive"
              checked={String(addForm.isActive) === "true"}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, isActive: e.target.checked ? "true" : "false" }))
              }
              style={{ width: "16px", height: "16px", marginRight: "8px", cursor: "pointer" }}
            />
            <label htmlFor="user-isactive" style={{ fontSize: "14px", fontWeight: "600", color: "#374151", cursor: "pointer" }}>
              Active User
            </label>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "32px",
            }}
          >
            <button
              onClick={() => setShowAdd(false)}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              {...createHoverHandlers("#f9fafb", "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={addLoading || !isFormValid()}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: (addLoading || !isFormValid()) ? "#9ca3af" : "#2563eb",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: (addLoading || !isFormValid()) ? "not-allowed" : "pointer",
                transition: "background-color 0.2s",
              }}
              {...(!addLoading && isFormValid() && createHoverHandlers("#1d4ed8", "#2563eb"))}
            >
              {addLoading ? "Saving..." : "Save User"}
            </button>
          </div>
        </div>
      </UserModal>

      {/* Delete Confirmation Modal */}
      <UserModal
        show={showDeleteModal}
        title="Delete User"
        onClose={() => setShowDeleteModal(false)}
      >
        <div>
          <p
            style={{ fontSize: "16px", color: "#6b7280", marginBottom: "24px" }}
          >
            Are you sure you want to delete this user?
          </p>
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            <button
              onClick={() => setShowDeleteModal(false)}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#f9fafb", "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteUser}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#ef4444",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#dc2626", "#ef4444")}
            >
              Delete
            </button>
          </div>
        </div>
      </UserModal>
      <ToastContainer
        containerId={USER_MGMT_TOAST_ID}
        position="top-right"
        autoClose={1500}
        style={{ zIndex: 11000 }}
      />

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
          Loading users...
        </div>
      ) : null}

      {!loading && error ? (
        <div style={{ textAlign: "center", padding: "48px", color: "#ef4444" }}>
          {error}
        </div>
      ) : null}

      {!loading && !error && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              backgroundColor: "white",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <thead>
              <tr
                style={{
                  background: "linear-gradient(to right, #eff6ff, #dbeafe)",
                }}
              >
                <th
                  onClick={() => setSortAsc(!sortAsc)}
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "700",
                    color: "#374151",
                    cursor: "pointer",
                    borderBottom: "2px solid #bfdbfe",
                  }}
                >
                  Name{" "}
                  <span style={{ marginLeft: "8px", fontSize: "12px" }}>
                    {sortAsc ? "▲" : "▼"}
                  </span>
                </th>
                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "700",
                    color: "#374151",
                    borderBottom: "2px solid #bfdbfe",
                  }}
                >
                  Email
                </th>

                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "700",
                    color: "#374151",
                    borderBottom: "2px solid #bfdbfe",
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "700",
                    color: "#374151",
                    borderBottom: "2px solid #bfdbfe",
                  }}
                >
                  Created Date
                </th>
                {(UserMgmt?.canDelete || UserMgmt?.canUpdate) && (
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "700",
                      color: "#374151",
                      borderBottom: "2px solid #bfdbfe",
                    }}
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {renderUsersTableContent()}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={getTableColSpan()}
                  style={{
                    padding: "16px 20px",
                    textAlign: "right",
                    fontSize: "12px",
                    color: "#6b7280",
                    backgroundColor: "#f9fafb",
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  Showing {pagedUsers.length} of {sortedUsers.length} user{sortedUsers.length === 1 ? "" : "s"}
                </td>
              </tr>
              {totalPages > 1 && (
                <tr>
                  <td
                    colSpan={getTableColSpan()}
                    style={{
                      padding: "16px 20px",
                      backgroundColor: "white",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          backgroundColor: page === 1 ? "#f3f4f6" : "white",
                          color: page === 1 ? "#9ca3af" : "#374151",
                          cursor: page === 1 ? "not-allowed" : "pointer",
                          fontSize: "14px",
                          fontWeight: "600",
                          transition: "all 0.2s",
                        }}
                        {...(page !== 1 && createHoverHandlers("#eff6ff", "white"))}
                        onFocus={(e) => {
                          if (page !== 1) {
                            e.currentTarget.style.borderColor = "#3b82f6";
                          }
                        }}
                        onBlur={(e) => {
                          if (page !== 1) {
                            e.currentTarget.style.borderColor = "#d1d5db";
                          }
                        }}
                      >
                        Prev
                      </button>

                      {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                          key={`page-${i + 1}`}
                          onClick={() => setPage(i + 1)}
                          style={{
                            padding: "8px 16px",
                            borderRadius: "6px",
                            border: `1px solid ${page === i + 1 ? "#1e40af" : "#d1d5db"
                              }`,
                            backgroundColor:
                              page === i + 1 ? "#1e40af" : "white",
                            color: page === i + 1 ? "white" : "#374151",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600",
                            minWidth: "40px",
                            transition: "all 0.2s",
                          }}
                          {...(page !== i + 1 && createHoverHandlers("#eff6ff", "white"))}
                          onFocus={(e) => {
                            if (page !== i + 1) {
                              e.currentTarget.style.borderColor = "#3b82f6";
                            }
                          }}
                          onBlur={(e) => {
                            if (page !== i + 1) {
                              e.currentTarget.style.borderColor = "#d1d5db";
                            }
                          }}
                        >
                          {i + 1}
                        </button>
                      ))}

                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          backgroundColor:
                            page === totalPages ? "#f3f4f6" : "white",
                          color: page === totalPages ? "#9ca3af" : "#374151",
                          cursor:
                            page === totalPages ? "not-allowed" : "pointer",
                          fontSize: "14px",
                          fontWeight: "600",
                          transition: "all 0.2s",
                        }}
                        {...(page !== totalPages && createHoverHandlers("#eff6ff", "white"))}
                        onFocus={(e) => {
                          if (page !== totalPages) {
                            e.currentTarget.style.borderColor = "#3b82f6";
                          }
                        }}
                        onBlur={(e) => {
                          if (page !== totalPages) {
                            e.currentTarget.style.borderColor = "#d1d5db";
                          }
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
